import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Plus, EditPencil, Trash, Check, Xmark, Search, Box, Cart,
} from "iconoir-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import {
    fetchStorageLocations,
    createStorageLocation,
    updateStorageLocation,
    deleteStorageLocation,
    fetchInventory,
    createInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,
} from "@/lib/api";

const CATEGORIES = ["Produce", "Dairy", "Meat", "Pantry", "Frozen", "Bakery", "Beverages", "Other"];
const UNITS = ["pcs", "kg", "g", "L", "mL", "bag", "can", "box", "bottle", "dozen"];

// Sort modes (order per user spec: Category · Recent · Expiring · A to Z)
const SORTS = [
    { id: "category", key: "inventory.sort.category" },
    { id: "recent", key: "inventory.sort.recent" },
    { id: "expiring", key: "inventory.sort.expiring" },
    { id: "alpha", key: "inventory.sort.alpha" },
];

function daysUntil(dateStr) {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(dateStr + "T00:00:00");
    if (isNaN(d.getTime())) return null;
    return Math.round((d.getTime() - today.getTime()) / 86400000);
}

function ExpiryBadge({ item, t }) {
    const d = daysUntil(item.expiration_date);
    const useSoon = daysUntil(item.use_soon_date);
    if (d === null && useSoon === null) return null;
    if (d !== null) {
        if (d < 0) return <span className="text-[10px] font-bold uppercase tracking-widest bg-[#fbd9d4] text-[#7a2a2a] px-1.5 py-0.5 rounded-full">{t("inventory.expired")}</span>;
        if (d === 0) return <span className="text-[10px] font-bold uppercase tracking-widest bg-[#fbeecb] text-[#7a5c1a] px-1.5 py-0.5 rounded-full">{t("inventory.expires_today")}</span>;
        if (d <= 3) return <span className="text-[10px] font-bold uppercase tracking-widest bg-[#fbeecb] text-[#7a5c1a] px-1.5 py-0.5 rounded-full">{t("inventory.expires_in_days", { n: d })}</span>;
        if (d <= 7) return <span className="text-[10px] font-bold uppercase tracking-widest bg-[#e8f1e4] text-[#1f4d2f] px-1.5 py-0.5 rounded-full">{t("inventory.expires_in_days", { n: d })}</span>;
    }
    if (useSoon !== null && useSoon <= 7) {
        return <span className="text-[10px] font-bold uppercase tracking-widest bg-[#e8e3f5] text-[#5d3b94] px-1.5 py-0.5 rounded-full">{t("inventory.use_soon_in_days", { n: Math.max(0, useSoon) })}</span>;
    }
    return null;
}

export default function InventoryTab({ session, onGoto }) {
    const { t } = useT();
    const hh = session?.household_id;

    const [locations, setLocations] = useState([]);
    const [items, setItems] = useState([]);
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState("category"); // default = category (first in row)
    const [show, setShow] = useState({}); // location_id -> bool ; lazily populated
    const [busy, setBusy] = useState(false);
    const [addingItem, setAddingItem] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [addingLocation, setAddingLocation] = useState(false);
    const [renamingLocation, setRenamingLocation] = useState(null);
    const [quickName, setQuickName] = useState("");

    const quickAddSubmit = async (e) => {
        e.preventDefault();
        const n = quickName.trim();
        if (!n) return;
        // Default location: if exactly one Show pill is ON, use that one. Otherwise first storage.
        const activeIds = Object.entries(show).filter(([, v]) => v).map(([k]) => k);
        const target = activeIds.length === 1 ? activeIds[0] : (locations[0]?.id || "");
        if (!target) {
            toast.error("Add a storage location first.");
            return;
        }
        try {
            const created = await createInventoryItem(hh, {
                name: n,
                location_id: target,
                quantity: null,
                unit: null,
                category: null, // backend auto-categorises
                expiration_date: null,
                use_soon_date: null,
                notes: null,
            });
            setItems((prev) => [created, ...prev]);
            setQuickName("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Add failed");
        }
    };

    const reload = async () => {
        if (!hh) return;
        setBusy(true);
        try {
            const [locs, its] = await Promise.all([
                fetchStorageLocations(hh),
                fetchInventory(hh),
            ]);
            setLocations(locs || []);
            setItems(its || []);
            // Initialise SHOW pills: all locations ON by default
            setShow((prev) => {
                const next = { ...prev };
                for (const l of locs || []) {
                    if (next[l.id] === undefined) next[l.id] = true;
                }
                return next;
            });
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hh]);

    const counts = useMemo(() => {
        const c = {};
        for (const it of items) c[it.location_id] = (c[it.location_id] || 0) + 1;
        return c;
    }, [items]);

    const filtered = useMemo(() => {
        // Hide items whose location pill is toggled OFF.
        // Items with an unknown location_id (legacy) are always shown.
        const visibleLocIds = new Set(
            Object.entries(show).filter(([, v]) => v).map(([k]) => k)
        );
        let list = items.filter((i) => !i.location_id || visibleLocIds.has(i.location_id));
        const q = query.trim().toLowerCase();
        if (q) list = list.filter((i) =>
            (i.name || "").toLowerCase().includes(q) ||
            (i.notes || "").toLowerCase().includes(q) ||
            (i.category || "").toLowerCase().includes(q)
        );
        const cmp = {
            recent: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
            alpha: (a, b) => (a.name || "").localeCompare(b.name || ""),
            category: (a, b) =>
                (a.category || "zzz").localeCompare(b.category || "zzz") ||
                (a.name || "").localeCompare(b.name || ""),
            expiring: (a, b) => {
                const ad = a.expiration_date || a.use_soon_date || "9999-12-31";
                const bd = b.expiration_date || b.use_soon_date || "9999-12-31";
                return ad.localeCompare(bd);
            },
        };
        return [...list].sort(cmp[sort] || cmp.category);
    }, [items, query, sort, show]);

    // Storage actions
    const saveLocation = async (payload) => {
        try {
            const created = await createStorageLocation(hh, payload);
            setLocations((prev) => [...prev, { ...created, item_count: 0 }]);
            setShow((prev) => ({ ...prev, [created.id]: true }));
            setAddingLocation(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not add storage");
        }
    };
    const renameLocation = async (id, payload) => {
        try {
            const updated = await updateStorageLocation(id, payload);
            setLocations((prev) => prev.map((l) => (l.id === id ? { ...l, ...updated } : l)));
            setRenamingLocation(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not rename");
        }
    };
    const removeLocation = async (loc) => {
        const count = counts[loc.id] || 0;
        if (count === 0) {
            if (!window.confirm(t("inventory.delete_location_confirm"))) return;
            try {
                await deleteStorageLocation(loc.id);
                setLocations((prev) => prev.filter((l) => l.id !== loc.id));
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not delete");
            }
            return;
        }
        const others = locations.filter((l) => l.id !== loc.id);
        if (!others.length) {
            toast.error("Add another storage location first so items have somewhere to go.");
            return;
        }
        const optionsText = others.map((l, i) => `${i + 1}. ${l.icon || ""} ${l.name}`).join("\n");
        const msg = t("inventory.delete_location_with_items", { n: count }) + "\n\n" + optionsText + "\n\n(Enter the number)";
        const choice = window.prompt(msg, "1");
        if (!choice) return;
        const idx = parseInt(choice, 10) - 1;
        if (Number.isNaN(idx) || idx < 0 || idx >= others.length) return;
        const moveTo = others[idx].id;
        try {
            await deleteStorageLocation(loc.id, moveTo);
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not delete");
        }
    };

    // Item actions
    const saveItem = async (payload) => {
        try {
            if (editingItem) {
                const updated = await updateInventoryItem(editingItem, payload);
                setItems((prev) => prev.map((i) => (i.id === editingItem ? updated : i)));
                setEditingItem(null);
            } else {
                const created = await createInventoryItem(hh, payload);
                setItems((prev) => [created, ...prev]);
                setAddingItem(false);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };
    const removeItem = async (item) => {
        if (!window.confirm(t("inventory.delete_item_confirm"))) return;
        try {
            await deleteInventoryItem(item.id);
            setItems((prev) => prev.filter((i) => i.id !== item.id));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const defaultLocationId = locations[0]?.id || "";

    return (
        <div className="space-y-5" data-testid="inventory-tab">
            {/* Title + Add */}
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs font-semibold text-[#5b6d63] uppercase tracking-widest">{t("inventory.tab_eyebrow")}</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight mt-1">{t("inventory.tab_title")}</h1>
                </div>
                {!addingItem && (
                    <button
                        data-testid="inventory-open-shopping-btn"
                        onClick={() => onGoto && onGoto("shopping")}
                        className="brut-btn brut-btn-secondary text-xs sm:text-sm whitespace-nowrap"
                    >
                        <Cart className="w-4 h-4" /> {t("inventory.open_shopping_btn")}
                    </button>
                )}
            </div>

            {/* Quick-add bar */}
            {!!locations.length && (
                <form onSubmit={quickAddSubmit} className="flex gap-2" data-testid="inventory-quick-form">
                    <input
                        data-testid="inventory-quick-input"
                        className="brut-input"
                        placeholder={t("inventory.add_placeholder")}
                        value={quickName}
                        onChange={(e) => setQuickName(e.target.value)}
                    />
                    <button data-testid="inventory-quick-submit" type="submit" className="brut-btn">
                        <Plus className="w-4 h-4" /> {t("inventory.add_btn")}
                    </button>
                </form>
            )}

            {/* Add/rename storage forms */}
            <AnimatePresence>
                {addingLocation && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                    >
                        <LocationForm onCancel={() => setAddingLocation(false)} onSave={saveLocation} t={t} testIdPrefix="new-loc" />
                    </motion.div>
                )}
            </AnimatePresence>
            <AnimatePresence>
                {renamingLocation && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                    >
                        <LocationForm
                            initial={renamingLocation}
                            onCancel={() => setRenamingLocation(null)}
                            onSave={(payload) => renameLocation(renamingLocation.id, payload)}
                            t={t}
                            testIdPrefix={`rename-loc-${renamingLocation.id}`}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Add item form */}
            <AnimatePresence>
                {addingItem && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                    >
                        <ItemForm
                            locations={locations}
                            defaultLocationId={defaultLocationId}
                            onCancel={() => setAddingItem(false)}
                            onSave={saveItem}
                            t={t}
                            testIdPrefix="new"
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Search */}
            <div className="relative">
                <Search className="w-4 h-4 text-[#5b6d63] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                    data-testid="inventory-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("inventory.search_placeholder")}
                    className="brut-input !pl-9 !py-2 text-sm w-full"
                />
            </div>

            {/* Sort row */}
            <div className="flex gap-2 overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6 scrollbar-hide" data-testid="inventory-sort-row">
                <span className="text-[10px] font-bold text-[#5b6d63] uppercase tracking-widest self-center shrink-0">{t("inventory.sort_label")}</span>
                {SORTS.map((s) => (
                    <button
                        key={s.id}
                        data-testid={`inventory-sort-${s.id}`}
                        onClick={() => setSort(s.id)}
                        className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${sort === s.id ? "bg-[#2F6B43] text-white" : "bg-white border border-[#0d2a1b]/8 hover:bg-[#faf5ea]"}`}
                    >
                        {t(s.key)}
                    </button>
                ))}
            </div>

            {/* Show (storage filter) pills */}
            <div className="flex gap-2 overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6 scrollbar-hide items-center" data-testid="inventory-show-row">
                <span className="text-[10px] font-bold text-[#5b6d63] uppercase tracking-widest self-center shrink-0">{t("inventory.show_label")}</span>
                {locations.map((loc) => {
                    const active = show[loc.id] !== false;
                    const c = counts[loc.id] || 0;
                    return (
                        <button
                            key={loc.id}
                            data-testid={`inventory-show-${loc.id}`}
                            onClick={() => setShow((s) => ({ ...s, [loc.id]: !s[loc.id] }))}
                            onDoubleClick={() => setRenamingLocation(loc)}
                            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${active ? "bg-[#2F6B43] text-white" : "bg-white border border-[#0d2a1b]/8 hover:bg-[#faf5ea] text-[#5b6d63] line-through"}`}
                            aria-pressed={active}
                        >
                            {loc.icon ? `${loc.icon} ` : ""}{loc.name} {c}
                        </button>
                    );
                })}
                <button
                    data-testid="inventory-add-location-btn"
                    onClick={() => setAddingLocation(true)}
                    className="shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border-2 border-dashed border-[#0d2a1b]/25 text-[#5b6d63] hover:bg-white"
                >
                    <Plus className="inline w-3.5 h-3.5 mr-0.5" /> {t("inventory.add_location")}
                </button>
            </div>

            {/* Items */}
            {busy && !items.length ? (
                <div className="brut-card p-6 text-center text-sm text-zinc-500">Loading…</div>
            ) : !locations.length ? (
                <div className="brut-card p-6 text-center text-sm text-zinc-500">
                    <Box className="w-8 h-8 mx-auto mb-3 text-[#2F6B43]" />
                    {t("inventory.empty_household")}
                </div>
            ) : !filtered.length ? (
                <div className="brut-card p-6 text-center text-sm text-zinc-500">{t("inventory.empty_location")}</div>
            ) : (
                <ul className="space-y-2">
                    <AnimatePresence>
                        {filtered.map((it) => (
                            <motion.li
                                key={it.id}
                                initial={{ opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                className="brut-card p-3"
                                data-testid={`inventory-card-${it.id}`}
                            >
                                {editingItem === it.id ? (
                                    <ItemForm
                                        initial={it}
                                        locations={locations}
                                        onCancel={() => setEditingItem(null)}
                                        onSave={saveItem}
                                        onDelete={() => removeItem(it)}
                                        t={t}
                                        testIdPrefix={it.id}
                                    />
                                ) : (
                                    <ItemRow
                                        it={it}
                                        location={locations.find((l) => l.id === it.location_id)}
                                        onEdit={() => { setEditingItem(it.id); setAddingItem(false); }}
                                        onRename={async (newName) => {
                                            try {
                                                const updated = await updateInventoryItem(it.id, { name: newName });
                                                setItems((prev) => prev.map((x) => (x.id === it.id ? updated : x)));
                                            } catch (err) {
                                                toast.error(err?.response?.data?.detail || "Rename failed");
                                            }
                                        }}
                                        t={t}
                                    />
                                )}
                            </motion.li>
                        ))}
                    </AnimatePresence>
                </ul>
            )}
        </div>
    );
}

function LocationForm({ initial, onCancel, onSave, t, testIdPrefix }) {
    const [name, setName] = useState(initial?.name || "");
    const [icon, setIcon] = useState(initial?.icon || "");
    const submit = (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        onSave({ name: name.trim(), icon: icon.trim() || null });
    };
    return (
        <form onSubmit={submit} className="brut-card p-4 space-y-2">
            <div className="flex gap-2">
                <input
                    data-testid={`${testIdPrefix}-icon`}
                    value={icon}
                    onChange={(e) => setIcon(e.target.value)}
                    placeholder="🥶"
                    maxLength={3}
                    className="brut-input !py-2 !text-base !w-14 text-center"
                />
                <input
                    data-testid={`${testIdPrefix}-name`}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("inventory.location_name_placeholder")}
                    autoFocus
                    className="brut-input !py-2 !text-sm flex-1"
                />
            </div>
            <div className="flex gap-2">
                <button type="submit" className="brut-btn text-xs flex-1 justify-center" data-testid={`${testIdPrefix}-save`}>
                    <Check className="w-3.5 h-3.5" /> {t("common.save")}
                </button>
                <button type="button" onClick={onCancel} className="brut-btn brut-btn-secondary text-xs" data-testid={`${testIdPrefix}-cancel`}>
                    <Xmark className="w-3.5 h-3.5" />
                </button>
            </div>
        </form>
    );
}

function ItemForm({ initial, locations, defaultLocationId, onCancel, onSave, onDelete, t, testIdPrefix }) {
    const [name, setName] = useState(initial?.name || "");
    const [location_id, setLocId] = useState(initial?.location_id || defaultLocationId || locations[0]?.id || "");
    const [quantity, setQuantity] = useState(initial?.quantity ?? "");
    const [unit, setUnit] = useState(initial?.unit || "");
    const [category, setCategory] = useState(initial?.category || "");
    const [expiration_date, setExp] = useState(initial?.expiration_date || "");
    const [use_soon_date, setUseSoon] = useState(initial?.use_soon_date || "");
    const [notes, setNotes] = useState(initial?.notes || "");

    const submit = (e) => {
        e.preventDefault();
        if (!name.trim() || !location_id) return;
        const q = quantity === "" ? null : Number(quantity);
        if (q !== null && Number.isNaN(q)) {
            toast.error("Quantity must be a number");
            return;
        }
        onSave({
            name: name.trim(),
            location_id,
            quantity: q,
            unit: unit || null,
            category: category || null,
            expiration_date: expiration_date || null,
            use_soon_date: use_soon_date || null,
            notes: notes.trim() || null,
        });
    };

    return (
        <form onSubmit={submit} className="brut-card p-4 space-y-2.5">
            <input
                data-testid={`item-name-${testIdPrefix}`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("inventory.fields.name_placeholder")}
                autoFocus
                className="brut-input !py-2 !text-sm w-full"
            />
            <div className="grid grid-cols-3 gap-2">
                <input
                    data-testid={`item-qty-${testIdPrefix}`}
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder={t("inventory.fields.quantity")}
                    className="brut-input !py-2 !text-sm"
                />
                <select
                    data-testid={`item-unit-${testIdPrefix}`}
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    className="brut-input !py-2 !text-sm"
                >
                    <option value="">{t("inventory.fields.unit")}</option>
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                <select
                    data-testid={`item-location-${testIdPrefix}`}
                    value={location_id}
                    onChange={(e) => setLocId(e.target.value)}
                    className="brut-input !py-2 !text-sm"
                >
                    {locations.map((l) => (
                        <option key={l.id} value={l.id}>{l.icon ? l.icon + " " : ""}{l.name}</option>
                    ))}
                </select>
            </div>
            <select
                data-testid={`item-cat-${testIdPrefix}`}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="brut-input !py-2 !text-sm w-full"
            >
                <option value="">{t("inventory.fields.category")}</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{t(`shop.cat.${c}`)}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#5b6d63] mb-0.5">{t("inventory.fields.expiration")}</div>
                    <input
                        data-testid={`item-exp-${testIdPrefix}`}
                        type="date"
                        value={expiration_date}
                        onChange={(e) => setExp(e.target.value)}
                        className="brut-input !py-2 !text-sm w-full"
                    />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#5b6d63] mb-0.5">{t("inventory.fields.use_soon")}</div>
                    <input
                        data-testid={`item-use-soon-${testIdPrefix}`}
                        type="date"
                        value={use_soon_date}
                        onChange={(e) => setUseSoon(e.target.value)}
                        className="brut-input !py-2 !text-sm w-full"
                    />
                </div>
            </div>
            <textarea
                data-testid={`item-notes-${testIdPrefix}`}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("inventory.fields.notes_placeholder")}
                rows={2}
                className="brut-input !py-2 !text-sm w-full"
            />
            <div className="flex gap-2 pt-1">
                <button type="submit" className="brut-btn text-xs flex-1 justify-center" data-testid={`item-save-${testIdPrefix}`}>
                    <Check className="w-3.5 h-3.5" /> {t("common.save")}
                </button>
                <button type="button" onClick={onCancel} className="brut-btn brut-btn-secondary text-xs" data-testid={`item-cancel-${testIdPrefix}`}>
                    <Xmark className="w-3.5 h-3.5" />
                </button>
                {onDelete && (
                    <button
                        type="button"
                        onClick={onDelete}
                        className="brut-btn brut-btn-secondary text-xs !text-[#A53F3F] hover:!bg-[#fbe0dc]"
                        data-testid={`item-delete-${testIdPrefix}`}
                        aria-label="delete item"
                    >
                        <Trash className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </form>
    );
}

function ItemRow({ it, location, onEdit, onRename, t }) {
    const [renaming, setRenaming] = useState(false);
    const [draft, setDraft] = useState(it.name);
    const commit = (e) => {
        e?.preventDefault?.();
        const n = (draft || "").trim();
        setRenaming(false);
        if (n && n !== it.name) onRename(n);
        else setDraft(it.name);
    };
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap">
                    {renaming ? (
                        <form onSubmit={commit} className="flex-1">
                            <input
                                data-testid={`item-rename-input-${it.id}`}
                                autoFocus
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onBlur={commit}
                                onKeyDown={(e) => { if (e.key === "Escape") { setRenaming(false); setDraft(it.name); } }}
                                className="brut-input !py-1 !text-sm w-full font-semibold"
                            />
                        </form>
                    ) : (
                        <button
                            type="button"
                            data-testid={`item-name-btn-${it.id}`}
                            onClick={() => { setDraft(it.name); setRenaming(true); }}
                            className="text-left hover:underline decoration-dotted underline-offset-4"
                            title={t("inventory.rename_hint")}
                        >
                            {it.name}
                        </button>
                    )}
                    {!renaming && it.quantity != null && (
                        <span className="text-[11px] text-[#5b6d63] tabular-nums font-bold">
                            {it.quantity}{it.unit ? ` ${it.unit}` : ""}
                        </span>
                    )}
                    {!renaming && <ExpiryBadge item={it} t={t} />}
                </div>
                <div className="text-[11px] text-[#5b6d63] mt-0.5 flex items-center gap-1.5 flex-wrap">
                    {location && <span>{location.icon ? location.icon + " " : ""}{location.name}</span>}
                    {it.category && <span>· {t(`shop.cat.${it.category}`)}</span>}
                    {it.notes && <span className="italic">· {it.notes}</span>}
                </div>
            </div>
            <button
                data-testid={`item-edit-${it.id}`}
                onClick={onEdit}
                className="w-8 h-8 rounded-full bg-[#faf5ea] hover:bg-[#e8f1e4] flex items-center justify-center shrink-0"
                aria-label="edit"
            >
                <EditPencil className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}
